import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { TID } from "../constants/testIds";
import { Plus, X, Building2 } from "lucide-react";

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", industry: "Technology", size: "mid", country: "United States", plan: "professional",
    admin_email: "", admin_name: "", admin_password: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tenants");
    setTenants(data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tenants", form);
      toast.success(`${form.name} onboarded`);
      setCreating(false);
      setForm({ ...form, name: "", slug: "", admin_email: "", admin_name: "", admin_password: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const suspend = async (t) => {
    try {
      await api.patch(`/tenants/${t.id}`, { status: t.status === "active" ? "suspended" : "active" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <SectionHeader
        title="Tenants"
        subtitle="Onboard, manage, and monitor every company on PropHR."
        right={<button className="btn-primary" onClick={() => setCreating(true)} data-testid={TID.createTenantBtn}><Plus size={16}/> Onboard tenant</button>}
      />

      {tenants.length === 0 ? (
        <EmptyState icon={Building2} title="No tenants yet" subtitle="Onboard your first customer to start operating PropHR." action={<button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16}/> Onboard first tenant</button>} />
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-4 py-3 eyebrow">Tenant</th>
                <th className="px-4 py-3 eyebrow">Slug</th>
                <th className="px-4 py-3 eyebrow">Industry</th>
                <th className="px-4 py-3 eyebrow">Size</th>
                <th className="px-4 py-3 eyebrow">Country</th>
                <th className="px-4 py-3 eyebrow">Plan</th>
                <th className="px-4 py-3 eyebrow">Retention</th>
                <th className="px-4 py-3 eyebrow">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} className="row-hover border-b border-slate-100 last:border-b-0" data-testid={`tenant-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{t.slug}</td>
                  <td className="px-4 py-3 text-slate-600">{t.industry || ","}</td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{t.size || ","}</td>
                  <td className="px-4 py-3 text-slate-600">{t.country || ","}</td>
                  <td className="px-4 py-3 font-mono text-xs uppercase">{t.plan}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.retention_days}d</td>
                  <td className="px-4 py-3">
                    <button onClick={() => suspend(t)} className={"pill " + (t.status === "active" ? "pill-resolved" : "pill-closed")}>
                      {t.status}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white border border-slate-300 max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold">Onboard new tenant</h3>
              <button type="button" onClick={() => setCreating(false)}><X size={18}/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Company name" required value={form.name} onChange={v => setForm({...form, name: v, slug: form.slug || v.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')})}/>
              <Field label="Slug (used in URLs)" required value={form.slug} onChange={v => setForm({...form, slug: v})}/>
              <Field label="Industry" value={form.industry} onChange={v => setForm({...form, industry: v})}/>
              <div>
                <label className="eyebrow block mb-1">Size</label>
                <select value={form.size} onChange={e => setForm({...form, size: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option value="small">Small (&lt;100)</option>
                  <option value="mid">Mid (100-1000)</option>
                  <option value="enterprise">Enterprise (1000+)</option>
                </select>
              </div>
              <Field label="Country" value={form.country} onChange={v => setForm({...form, country: v})}/>
              <div>
                <label className="eyebrow block mb-1">Plan</label>
                <select value={form.plan} onChange={e => setForm({...form, plan: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option>starter</option><option>professional</option><option>enterprise</option>
                </select>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="eyebrow mb-3">Bootstrap Company Admin</div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Admin name" required value={form.admin_name} onChange={v => setForm({...form, admin_name: v})}/>
                <Field label="Admin email" required type="email" value={form.admin_email} onChange={v => setForm({...form, admin_email: v})}/>
                <Field label="Admin password" required type="password" value={form.admin_password} onChange={v => setForm({...form, admin_password: v})} className="md:col-span-2"/>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving} data-testid={TID.createTenantSubmit}>
                {saving ? "Onboarding…" : "Onboard tenant"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "" }) {
  return (
    <div className={className}>
      <label className="eyebrow block mb-1">{label}</label>
      <input required={required} type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm"/>
    </div>
  );
}
